/**
 * Context-scoped chat store.
 *
 * The chat store used to be a process-wide singleton (`zustand/create`).
 * That works for one chat at a time, but breaks the moment two chats need
 * to coexist — for example, the agent builder's test panel rendering its
 * own MessageList alongside the main chat. The test panel ended up
 * hijacking the singleton's `activeSessionId`, which fired side-effects
 * in ChatPage and unmounted the builder mid-render.
 *
 * The fix is to make the store a per-subtree resource. `<ChatStoreProvider>`
 * lazily creates a vanilla store via `createChatStore()` on first mount
 * and exposes it through context. Hooks that need imperative access read
 * the store ref via `useChatStoreApi()`; hooks that only need to react to
 * state use `useChatStore(selector)`.
 *
 * The root layout wraps the app once so all routes share a "main" store.
 * AgentBuilder nests its own provider so its test session is fully isolated.
 */

import { createContext, useContext, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { createChatStore, type ChatStore, type ChatStoreApi } from "./chat-store";

const ChatStoreContext = createContext<ChatStoreApi | null>(null);

interface ChatStoreProviderProps {
  children: ReactNode;
  /** Distinct devtools tag — handy for telling main vs. test stores apart. */
  devtoolsName?: string;
}

export function ChatStoreProvider({ children, devtoolsName }: ChatStoreProviderProps) {
  // useState init runs exactly once per provider instance — that's our
  // per-subtree singleton. Unmounting the provider drops the store.
  const [store] = useState(() => createChatStore(devtoolsName));
  return <ChatStoreContext.Provider value={store}>{children}</ChatStoreContext.Provider>;
}

/**
 * Get the raw store API for the surrounding subtree. Use this in callbacks
 * and effects that need `getState()`/`setState()` — they're imperative and
 * must not subscribe to renders.
 */
export function useChatStoreApi(): ChatStoreApi {
  const store = useContext(ChatStoreContext);
  if (!store) {
    throw new Error("useChatStoreApi must be used inside <ChatStoreProvider>");
  }
  return store;
}

/**
 * Subscribe to a slice of the store. Same ergonomics as the old
 * `useChatStore(selector)` from zustand's `create`.
 */
export function useChatStore<T>(selector: (s: ChatStore) => T): T {
  const store = useChatStoreApi();
  return useStore(store, selector);
}
